import type { ReactNode } from 'react'

interface ProcessingSectionProps {
  testId: string
  title: string
  count: number
  pendingCount?: number
  failedCount?: number
  runningCount?: number
  info?: string
  expanded: boolean
  onToggle: () => void
  onResetFailures?: () => void
  children: ReactNode
}

function ChevronIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3l5 5-5 5" />
    </svg>
  )
}

function ProcessingSection({ testId, title, count, pendingCount, failedCount, runningCount, info, expanded, onToggle, onResetFailures, children }: ProcessingSectionProps) {
  return (
    <div data-testid={testId} className="processing-section">
      <div
        data-testid={`${testId}-header`}
        className={`processing-section-header${expanded ? ' processing-section-header--sticky' : ''}`}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle() } }}
      >
        <span className={`processing-section-chevron ${expanded ? 'expanded' : ''}`}>
          <ChevronIcon />
        </span>
        <span className="processing-section-title">{title}</span>
        {runningCount !== undefined && runningCount > 0 && (
          <span data-testid={`${testId}-running-badge`} className="processing-section-badge running">
            {runningCount} running
          </span>
        )}
        {info && (
          <span className="processing-section-info">{info}</span>
        )}
        {pendingCount !== undefined && failedCount !== undefined ? (
          <>
            {pendingCount > 0 && (
              <span data-testid={`${testId}-pending-badge`} className="processing-section-badge has-items">
                {pendingCount} pending
              </span>
            )}
            {failedCount > 0 && (
              <span data-testid={`${testId}-failed-badge`} className="processing-section-badge failed">
                {failedCount} failed
              </span>
            )}
            {failedCount > 0 && onResetFailures && (
              <button
                data-testid={`${testId}-reset-btn`}
                className="processing-section-reset-btn"
                onClick={(e) => { e.stopPropagation(); onResetFailures() }}
              >
                Reset Failures
              </button>
            )}
          </>
        ) : (
          count > 0 && (
            <span data-testid={`${testId}-badge`} className="processing-section-badge has-items">
              {count}
            </span>
          )
        )}
      </div>
      {expanded && (
        <div className="processing-section-content">
          {children}
        </div>
      )}
    </div>
  )
}

export default ProcessingSection
